import * as pdfjsLib from 'pdfjs-dist';
import { LoggerService } from '@optima-stride-ai/core';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.449/build/pdf.worker.min.mjs';

/**
 * Utility class for generating PDF thumbnails using PDF.js
 * This utility handles PDF thumbnail generation without requiring Angular components
 */
export class PdfThumbnailGenerator {
  static http: any;
  static logger: LoggerService;
  
  /**
   * Generate thumbnail from PDF file
   * @param file - PDF file to generate thumbnail from
   * @param options - Optional configuration for thumbnail generation
   * @returns Promise resolving to base64 data URL of the thumbnail
   */
  static async generateThumbnailFromFile(
    file: File, 
    options: {
      scale?: number;
      format?: 'png' | 'jpg';
      quality?: number;
    } = {}
  ): Promise<string | null> {
    try {
      const { scale = 0.3, format = 'png', quality = 0.8 } = options;
      
      this.logger?.debug(`🔄 Starting PDF thumbnail generation for file: ${file.name}`);
      
      // Convert file to ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Load PDF from ArrayBuffer
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        isEvalSupported: false,
        disableAutoFetch: true,
        disableStream: true
      });
      
      const pdf = await loadingTask.promise;
      this.logger?.debug(`✅ PDF loaded successfully, pages: ${pdf.numPages}`);

      // Get first page
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale });

      // Create canvas
      const canvas: HTMLCanvasElement = document.createElement('canvas');
      const context = canvas.getContext('2d')!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // Render page to canvas
      await page.render({ 
        canvasContext: context, 
        viewport,
        canvas: canvas 
      }).promise;
      this.logger?.debug('✅ PDF page rendered to canvas');

      // Convert canvas to image
      const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
      const thumbnail = canvas.toDataURL(mimeType, quality);
      this.logger?.info('✅ PDF thumbnail generated successfully');
      
      return thumbnail;
    } catch (error) {
      this.logger?.error(`❌ Error generating PDF thumbnail: ${error}`);
      return null;
    }
  }

  /**
   * Generate thumbnail from PDF URL
   * @param pdfUrl - URL of the PDF file
   * @param options - Optional configuration for thumbnail generation
   * @returns Promise resolving to base64 data URL of the thumbnail
   */
  static async generateThumbnailFromUrl(
    pdfUrl: string,
    options: {
      scale?: number;
      format?: 'png' | 'jpeg';
      quality?: number;
    } = {}
  ): Promise<string | null> {
    try {
      const { scale = 0.3, format = 'png', quality = 0.8 } = options;
      
      this.logger?.debug(`🔄 Starting PDF thumbnail generation for URL: ${pdfUrl}`);
      
      // Load PDF from URL
      const loadingTask = pdfjsLib.getDocument({
        url: pdfUrl,
        isEvalSupported: false,
        disableAutoFetch: true,
        disableStream: true
      });
      
      const pdf = await loadingTask.promise;
      this.logger?.debug(`✅ PDF loaded successfully, pages: ${pdf.numPages}`);

      // Get first page
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale });

      // Create canvas
      const canvas: HTMLCanvasElement = document.createElement('canvas');
      const context = canvas.getContext('2d')!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // Render page to canvas
      await page.render({ 
        canvasContext: context, 
        viewport,
        canvas: canvas 
      }).promise;
      this.logger?.debug('✅ PDF page rendered to canvas');

      // Convert canvas to image
      const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
      const thumbnail = canvas.toDataURL(mimeType, quality);
      this.logger?.info('✅ PDF thumbnail generated successfully');
      
      return thumbnail;
    } catch (error) {
      this.logger?.error(`❌ Error generating PDF thumbnail: ${error}`);
      return null;
    }
  }

  /**
   * Check if the provided file is a PDF
   * @param file - File to check
   * @returns true if file is PDF, false otherwise
   */
  static isPdfFile(file: File): boolean {
    this.logger?.debug(`🔍 Checking if file is a PDF: ${file.name}, ${file.name.toLowerCase().endsWith('.pdf')}`);
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  }

  /**
   * Generate fallback thumbnail for when PDF processing fails
   * @param filename - Name of the PDF file
   * @returns Base64 data URL of a fallback thumbnail
   */
  static generateFallbackThumbnail(filename: string): string {

    return this.http.post('/generate-fallback-thumbnail', { filename }).subscribe((response: any) => {
          this.logger?.info(`✅ Fallback thumbnail generated from server: ${response}`);
          return response.dataUrl;
      }, (error: any) => {
          this.logger?.error(`❌ Error generating fallback thumbnail from server: ${error}`);
          return '';
      });
}}